"use client";

import { useCallback, useEffect, useState } from "react";
import Script from "next/script";
import { usePaywall } from "@/app_lib/providers/PaywallProvider";
import { verifyGoogleIdToken, createGoogleUserSession } from "services/GoogleOneTap/verifyToken";
import { CookieService } from "@/app_lib/CookieService";
import type { GoogleOneTapCredentialResponse, GoogleAuthResult } from "services/GoogleOneTap/types";
import { trackEvent } from '@/app_lib/services/Gtm/utils';
import { trackOptimizelyEvent } from "providers/OptimizelyProvider";
import type { PageType } from '@/app_lib/types/Post';
import useBreakpoint from "hooks/useBreakpoint";
import { userTokenCookieDomain } from "@/app_lib/utils/clientCookieUtils";
import { OPTIMIZELY_EVENT_VALUES } from "providers/OptimizelyProvider/utils";

/**
 * GoogleOneTap component for Google One Tap authentication.
 * App Router compatible, strongly typed, and focused on a single responsibility.
 *
 * @param {GoogleOneTapProps} props - Component props
 * @returns {JSX.Element | null}
 */
export interface GoogleOneTapProps {
  clientId: string;
  onSuccess?: (userData: GoogleAuthResult) => void;
  onError?: (error: string) => void;
  pageType: PageType;
}

const GoogleOneTap: React.FC<GoogleOneTapProps> = ({ clientId, onSuccess, onError, pageType }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [oneTrustConsentReady, setOneTrustConsentReady] = useState<boolean | null>(null);
  const isMobile = useBreakpoint("mdMax");
  const isTablet = useBreakpoint("lgMax");

  const {
    paywallState: { paywallCheck },
  } = usePaywall();

  const deviceType = isMobile ? "mobile" : isTablet ? "tablet" : "desktop";

  useEffect(() => {
    const utpCookie = CookieService.get("__utp");
    setIsLoggedIn(Boolean(utpCookie));

    const checkOneTrustClosed = (callback: (result: boolean) => void) => {
      setTimeout(() => {
        const oneTrustApi = (window as any).OneTrust;
        if (!oneTrustApi?.IsAlertBoxClosed || typeof oneTrustApi.IsAlertBoxClosed !== "function") {
          callback(true);
          return;
        }
        const isAlertBoxClosed = oneTrustApi.IsAlertBoxClosed();
        callback(isAlertBoxClosed);
      }, 50);
    };

    const handleOneTrustGroupsUpdated = () => {
      checkOneTrustClosed((isOneTrustClosed) => {
        setOneTrustConsentReady(isOneTrustClosed);
      });
    };

    window.addEventListener("OneTrustGroupsUpdated", handleOneTrustGroupsUpdated as EventListener);
    checkOneTrustClosed((isOneTrustClosed) => {
      setOneTrustConsentReady(isOneTrustClosed);
    });

    return () => {
      window.removeEventListener("OneTrustGroupsUpdated", handleOneTrustGroupsUpdated as EventListener);
    };
  }, []);

  const handleAuthError = useCallback(
    (error: string, errorType: string) => {
      trackEvent({
        eventLabel: "google_one_tap_login_error",
        eventAction: "error/drop-off",
        customData: {
          pageType,
          deviceType,
          user_registration_type: "google_one_tap",
          error: errorType,
        },
      });
      onError?.(error);
    },
    [pageType, deviceType, onError]
  );

  const handleSuccessfulAuth = useCallback(
    async (sessionResult: any) => {
      if (sessionResult.pianoToken && sessionResult.pianoUid) {
        const { domain } = userTokenCookieDomain();
        CookieService.set("__utp", sessionResult.pianoToken, {
          secure: true,
          sameSite: "strict",
          ...(domain && { domain }),
        });
      }
      setIsLoggedIn(true);
      const successData: GoogleAuthResult = {
        success: true,
        pianoUid: sessionResult.pianoUid,
        accessToken: sessionResult.pianoToken,
        isNewUser: sessionResult.isNewUser,
        message: sessionResult.message,
      };
      trackEvent({
        eventLabel: "google_one_tap_login_success",
        eventAction: "conversion",
        customData: {
          pageType,
          deviceType,
          isNewUser: JSON.stringify(sessionResult.isNewUser),
          user_registration_type: "google_one_tap",
        },
      });
      trackOptimizelyEvent("user_signed_in", {});
      if (sessionResult.isNewUser) {
        trackEvent({
          eventLabel: "google_one_tap_user_created",
          eventAction: "conversion",
          customData: {
            pageType,
            deviceType,
            isNewUser: JSON.stringify(sessionResult.isNewUser),
            user_registration_type: "google_one_tap",
          },
        });
        trackOptimizelyEvent("user_signed_up", { value: OPTIMIZELY_EVENT_VALUES.signUpValue });
      }
      onSuccess?.(successData);
      setTimeout(() => {
        window.location.reload();
      }, 100);
    },
    [pageType, deviceType, onSuccess]
  );

  const processAuthentication = useCallback(
    async (credential: string) => {
      const verificationResult = await verifyGoogleIdToken(credential);
      if (!verificationResult.success) {
        handleAuthError(verificationResult.error || "Token verification failed", "token_verification_failed");
        return;
      }
      trackEvent({
        eventLabel: "google_one_tap_credential_received",
        eventAction: "click/engagement",
        customData: { pageType, deviceType, user_registration_type: "google_one_tap" },
      });
      trackOptimizelyEvent("google_one_tap_continue", {});
      if (!verificationResult.payload) {
        handleAuthError("Invalid token payload", "invalid_token_payload");
        return;
      }
      const sessionResult = await createGoogleUserSession(verificationResult.payload);
      if (!sessionResult.success) {
        handleAuthError(sessionResult.error || "Session creation failed", "session_creation_failed");
        return;
      }
      await handleSuccessfulAuth(sessionResult);
    },
    [handleAuthError, handleSuccessfulAuth, pageType, deviceType]
  );

  const handleCredentialResponse = useCallback(
    async (response: GoogleOneTapCredentialResponse) => {
      if (isProcessing || isLoggedIn) return;
      setIsProcessing(true);
      try {
        await processAuthentication(response.credential);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to process Google One Tap response";
        onError?.(errorMessage);
      } finally {
        setIsProcessing(false);
      }
    },
    [isProcessing, isLoggedIn, processAuthentication, onError]
  );

  function momentCallback(notification: any) {
    if (notification.isDisplayed?.()) {
      trackOptimizelyEvent("google_one_tap_shown", {});
      trackEvent({
        eventLabel: "google_one_tap_prompt_shown",
        eventAction: "impression",
        customData: {
          pageType,
          deviceType,
          user_registration_type: "google_one_tap",
        },
      });
    }
    if (notification.isDismissedMoment?.()) {
      trackOptimizelyEvent("google_one_tap_dismissed", { value: notification.getDismissedReason?.() || "unknown" });
      trackEvent({
        eventLabel: "google_one_tap_prompt_dismissed",
        eventAction: `click/engagement${notification.getDismissedReason ? ` - ${notification.getDismissedReason()}` : ""}`,
        customData: {
          pageType,
          deviceType,
          user_registration_type: "google_one_tap",
        },
      });
    }
    if (notification.isSkippedMoment?.()) {
      trackOptimizelyEvent("google_one_tap_skipped", { value: notification.getSkippedReason?.() || "unknown" });
      trackEvent({
        eventLabel: "google_one_tap_prompt_skipped",
        eventAction: `click outside/engagement${notification.getSkippedReason ? ` - ${notification.getSkippedReason()}` : ""}`,
        customData: {
          pageType,
          deviceType,
          user_registration_type: "google_one_tap",
        },
      });
    }
  }

  useEffect(() => {
    if (
      paywallCheck === "paywalled" ||
      paywallCheck === "pending" ||
      isLoggedIn ||
      isProcessing ||
      !oneTrustConsentReady
    ) {
      return;
    }
    const initializeGoogleOneTap = () => {
      if (window.google?.accounts?.id) {
        const config: any = {
          client_id: clientId,
          context: "signin",
          cancel_on_tap_outside: true,
          auto_select: false,
          use_fedcm_for_prompt: true,
        };
        config.callback = handleCredentialResponse;
        config.moment_callback = momentCallback;
        window.google.accounts.id.initialize(config);
        (window.google.accounts.id.prompt as any)(momentCallback);
      }
    };
    let observer: IntersectionObserver | null = null;
    if (isMobile) {
      const relatedVideoElement = document.getElementById("related-video");
      if (relatedVideoElement) {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && window.google?.accounts?.id?.cancel) {
                window.google.accounts.id.cancel();
              }
            });
          },
          { threshold: 0.1 }
        );
        observer.observe(relatedVideoElement);
      }
    }
    if (window.google?.accounts?.id) {
      initializeGoogleOneTap();
    } else {
      const checkGoogleLoaded = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(checkGoogleLoaded);
          initializeGoogleOneTap();
        }
      }, 50);
      return () => {
        clearInterval(checkGoogleLoaded);
        observer?.disconnect();
      };
    }
    return () => {
      observer?.disconnect();
    };
  }, [clientId, handleCredentialResponse, paywallCheck, isLoggedIn, isProcessing, oneTrustConsentReady, isMobile]);

  if (isLoggedIn === null || oneTrustConsentReady === null) return null;
  if (isLoggedIn || isProcessing || !oneTrustConsentReady) return null;

  return (
    <Script src="https://accounts.google.com/gsi/client" strategy="afterInteractive" />
  );
};

export default GoogleOneTap;

